import { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { validatePasswordStrength } from "@/lib/commonPasswords";
import { AlertCircle, CheckCircle2 } from "lucide-react";

interface PasswordStrengthMeterProps {
  password: string;
  onValidationChange?: (isValid: boolean) => void;
}

export function PasswordStrengthMeter({ password, onValidationChange }: PasswordStrengthMeterProps) {
  const [validation, setValidation] = useState({ isValid: false, errors: [], score: 0 });

  useEffect(() => {
    if (password) {
      const result = validatePasswordStrength(password);
      setValidation(result);
      onValidationChange?.(result.isValid);
    } else {
      setValidation({ isValid: false, errors: [], score: 0 });
      onValidationChange?.(false);
    }
  }, [password, onValidationChange]);

  if (!password) return null;

  const strengthLabel = validation.score >= 5 ? 'Strong' : validation.score >= 3 ? 'Medium' : 'Weak';
  const strengthColor = validation.score >= 5 ? 'text-green-600' : validation.score >= 3 ? 'text-yellow-600' : 'text-red-600';
  const progressValue = (validation.score / 6) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Password Strength:</span>
        <span className={`text-sm font-medium ${strengthColor}`}>{strengthLabel}</span>
      </div>
      
      <Progress value={progressValue} className="h-2" />
      
      {validation.errors.length > 0 && (
        <div className="space-y-1">
          {validation.errors.map((error, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}
      
      {validation.isValid && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle2 className="h-4 w-4" />
          <span>Password meets all requirements</span>
        </div>
      )}
    </div>
  );
}
